import { useTranslate } from "@tolgee/react";
import {
	AlertTriangle,
	ArrowDownToLine,
	ArrowUpRight,
	Check,
	Copy,
	LogOut,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { RefreshAssetsButton } from "#/components/refresh-assets-button";
import { Button } from "#/components/ui/button";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "#/components/ui/popover";
import { DepositDialog } from "#/features/trade/components/order-form/deposit-dialog.tsx";
import { DepositWarningIndicator } from "#/features/trade/components/order-form/deposit-warning-indicator.tsx";
import { WithdrawDialog } from "#/features/trade/components/order-form/withdraw-dialog.tsx";
import { WithdrawStatusIndicator } from "#/features/trade/components/order-form/withdraw-status-indicator.tsx";
import { useAuth } from "#/hooks/use-auth";
import { useHyperAccount } from "#/hooks/use-hyperliquid-account";
import {
	useDeposit,
	useExternalWithdraw,
	useTransferToWallet,
} from "#/hooks/use-trading-actions";
import { useUnifiedBalance } from "#/hooks/use-unified-balance";
import { useWallet } from "#/hooks/use-wallet";
import { useWalletBalance } from "#/hooks/use-wallet-balance";
import { formatSize, formatUsd, maskEmail, toNumber } from "#/lib/formatters";
import { AccountTypeDialog } from "./account-type-dialog.tsx";

export default function AccountMenu() {
	const { t } = useTranslate();
	const wallet = useWallet();
	const { logout } = useAuth();
	const { accountState } = useHyperAccount();
	const { isUnified, isDexAbstraction, spotUsdc, spotUsdcHold } =
		useUnifiedBalance();
	const { balance: walletBalance, usdtBalance } = useWalletBalance();
	const depositAction = useDeposit();
	const transferToWalletAction = useTransferToWallet();
	const externalWithdrawAction = useExternalWithdraw();
	const [menuOpen, setMenuOpen] = useState(false);
	const [copied, setCopied] = useState(false);

	const perpAccountValue = toNumber(
		accountState?.marginSummary?.accountValue ??
			accountState?.crossMarginSummary.accountValue,
	);
	// True equity = positions MTM + free spot. For unified, spotUsdc.hold
	// is already represented inside perpAccountValue (hold IS the position
	// margin), so `spot - hold` extracts only the free portion. For
	// dexAbstraction, main-side spot.hold is typically 0, so the same
	// formula degrades cleanly. See ACCOUNT.md → Balance display rules.
	const tradingBalance =
		isUnified || isDexAbstraction
			? perpAccountValue + spotUsdc - spotUsdcHold
			: perpAccountValue;
	const equity = accountState ? formatUsd(tradingBalance) : null;

	const menuItemClassName =
		"w-full justify-start border border-transparent text-muted-foreground hover:border-transparent dark:hover:bg-[var(--color-theme-fill-strong)]";

	if (wallet.isConnected) {
		return (
			<Popover open={menuOpen} onOpenChange={setMenuOpen}>
				<PopoverTrigger asChild>
					<Button
						variant="secondary"
						size="sm"
						title={wallet.address ?? undefined}
						className="bg-[var(--color-theme-fill-accent)] px-2 gap-1 sm:px-4 sm:gap-3"
					>
						{equity && (
							<span className="text-[var(--color-theme-status-positive)]">
								{equity}
							</span>
						)}
						<span>{wallet.displayAddress}</span>
					</Button>
				</PopoverTrigger>
				<PopoverContent
					align="end"
					sideOffset={4}
					className="min-w-52 rounded-md border border-border bg-card dark:border-[var(--color-theme-line-default)] dark:bg-[var(--color-theme-bg-elevated)] p-1 shadow-lg"
				>
					{wallet.email && (
						<div className="flex items-center justify-between gap-2 px-3 pt-2 pb-1">
							<span className="min-w-0 truncate text-caption1 text-muted-foreground">
								{maskEmail(wallet.email)}
							</span>
							<RefreshAssetsButton />
						</div>
					)}

					{/* Balances */}
					<div className="px-3 py-2 text-label2">
						<div className="flex items-center justify-between gap-4 text-muted-foreground">
							<span>{t("account.trading-account")}</span>
							<span className="flex items-center gap-1 text-label2-medium text-foreground">
								{equity ?? "$0.00"}
							</span>
						</div>
						<div className="mt-1 flex items-center justify-between gap-4 text-muted-foreground">
							<span>{t("account.wallet")}</span>
							<span className="flex items-center gap-1 text-label2-medium text-foreground">
								{`${formatSize(walletBalance, 2)} USDC`}
							</span>
						</div>
						{usdtBalance > 1 && (
							<div className="mt-1 flex items-center justify-between gap-4 text-muted-foreground">
								<span>{t("account.wallet")}</span>
								<span className="text-label2-medium text-foreground">
									{formatSize(usdtBalance, 2)} USDT
								</span>
							</div>
						)}
					</div>

					{usdtBalance > 1 && (
						<div className="flex items-start gap-2 px-3 py-2 text-caption1 text-[var(--color-theme-denotives-yellow)]">
							<AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
							<span>{t("account.usdt-swap-warning")}</span>
						</div>
					)}

					<Button
						variant="ghost"
						size="xs"
						onClick={() => {
							if (wallet.address) {
								navigator.clipboard.writeText(wallet.address);
								setCopied(true);
								setTimeout(() => setCopied(false), 1500);
								toast.success(t("actions.copied"));
							}
						}}
						className={menuItemClassName}
					>
						{copied ? (
							<Check className="h-3 w-3" />
						) : (
							<Copy className="h-3 w-3" />
						)}
						{copied ? t("actions.copied") : t("actions.copy-address")}
					</Button>

					<div className="my-1 border-t border-border" />

					{/* Actions */}
					<DepositDialog
						onConfirm={(amount) => depositAction.mutateAsync(amount)}
					>
						<Button variant="ghost" size="xs" className={menuItemClassName}>
							<ArrowDownToLine className="h-3 w-3" />
							{t("actions.deposit-to-trading")}
							<DepositWarningIndicator className="ml-auto h-3 w-3" />
						</Button>
					</DepositDialog>

					<WithdrawDialog
						onTransferToWallet={async (amount) => {
							await transferToWalletAction.mutateAsync(amount);
						}}
						onExternalWithdraw={async ({ amount, destination }) => {
							await externalWithdrawAction.mutateAsync({ amount, destination });
						}}
					>
						<Button variant="ghost" size="xs" className={menuItemClassName}>
							<ArrowUpRight className="h-3 w-3" />
							{t("actions.withdraw")}
							<WithdrawStatusIndicator className="ml-auto h-3 w-3" />
						</Button>
					</WithdrawDialog>

					<AccountTypeDialog triggerClassName={menuItemClassName} />

					<div className="my-1 border-t border-border" />

					<Button
						variant="ghost"
						size="xs"
						onClick={() => {
							logout();
							setMenuOpen(false);
						}}
						className={menuItemClassName}
					>
						<LogOut className="h-3 w-3" />
						{t("actions.disconnect")}
					</Button>
				</PopoverContent>
			</Popover>
		);
	}

	return (
		<Button
			variant="secondary"
			size="sm"
			onClick={() => wallet.connect("header")}
			disabled={!wallet.isReady}
			aria-busy={!wallet.isReady}
		>
			{t("actions.login")}
		</Button>
	);
}
